// Phase 15 Plan 06 Task 3 — Ranked matchmaking enqueue modal.
//
// Same shape as CasualEnqueueModal + a 3-ladder selector at top (Standard
// 4L / Open 3L / Purist per DEC-010 — the ranked-ladder enum from the
// OpenAPI matchmakingEnqueue request body: 'standard_4' | 'open_3' |
// 'purist'). The ladder selection feeds the `ruleset` field on the
// enqueue request.
//
// State machine + DELETE-on-close mirror CasualEnqueueModal (Pattern S4).
//
// gap-MUI-03 (milestone-audit fix): mounts LoadoutPickerModal as a pre-
// submit step so the user picks their 4-of-8 ranked loadout before the
// enqueue fires. The selection is persisted to localStorage via
// setLoadoutForMode('ranked', ...) by the picker itself (DEC-024
// per-mode preset). The /api/matchmaking/enqueue request body schema
// is `additionalProperties: false` and does NOT accept powerupLoadout
// — the persisted preset is what the server reads when the actual
// ranked match is created server-side after pairing. The bot-offer
// fallback path (/api/match/create) DOES accept powerupLoadout and
// the user's selection is forwarded there explicitly.

import { useStore } from '@nanostores/react'
import { type JSX, useCallback, useEffect, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import type { PowerUpName } from '../../game/PlayerViewSchema'
import { ApiError, apiFetch } from '../../net/client'
import { createIdempotencyKey } from '../../net/idempotency'
import { useRouter } from '../../router/router'
import { $locale } from '../../stores/locale'
import { Btn } from '../components/Btn'
import { Seg, type SegOption } from '../components/Seg'
import { Sheet } from '../components/Sheet'
import { LoadoutPickerModal } from '../match/LoadoutPickerModal'

// All 8 power-ups — used as `availablePowerups` for the picker on the
// ranked pre-match surface. The host doesn't host-filter on the ranked
// ladder; DEC-024 caps at 4-of-8 server-side, all 8 are eligible.
const ALL_POWERUPS: readonly PowerUpName[] = [
  'scriveners_quill',
  'tongue_tie',
  'knights_vow',
  'whisperers_promise',
  'lexical_mulligan',
  'the_vanishing',
  'castle_keep',
  'anagram_sigil',
]

type Ladder = 'standard_4' | 'open_3' | 'purist'
type MatchmakingState =
  | 'idle'
  | 'enqueuing'
  | 'waiting'
  | 'matched'
  | 'bot_offer'
  | 'error'

interface EnqueueResponse {
  readonly ticket: string
}
interface StatusResponse {
  readonly state: 'waiting' | 'matched' | 'expired'
  readonly matchId?: string
}
interface MatchCreateResponse {
  readonly matchId: string
}

export interface RankedEnqueueModalProps {
  readonly open: boolean
  readonly onClose: () => void
}

const POLL_INTERVAL_MS = 2000

const LADDER_OPTIONS: ReadonlyArray<SegOption<Ladder>> = [
  {
    value: 'standard_4',
    label: (
      <FormattedMessage
        id="social.leaderboard.ladder.standard_4l"
        defaultMessage="Standard 4L"
      />
    ),
    ariaLabel: 'Standard 4L',
  },
  {
    value: 'open_3',
    label: (
      <FormattedMessage
        id="social.leaderboard.ladder.open_3l"
        defaultMessage="Open 3L"
      />
    ),
    ariaLabel: 'Open 3L',
  },
  {
    value: 'purist',
    label: (
      <FormattedMessage
        id="social.leaderboard.ladder.purist"
        defaultMessage="Purist"
      />
    ),
    ariaLabel: 'Purist',
  },
]

export function RankedEnqueueModal({
  open,
  onClose,
}: RankedEnqueueModalProps): JSX.Element | null {
  const router = useRouter()
  const intl = useIntl()
  const locale = useStore($locale)
  const [ladder, setLadder] = useState<Ladder>('standard_4')
  const [state, setState] = useState<MatchmakingState>('idle')
  const [loadout, setLoadout] = useState<readonly PowerUpName[] | null>(null)
  const [loadoutPickerOpen, setLoadoutPickerOpen] = useState<boolean>(false)
  const ticketRef = useRef<string | null>(null)
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const mountedRef = useRef<boolean>(false)

  const clearPollTimer = useCallback((): void => {
    if (pollTimerRef.current !== null) {
      clearInterval(pollTimerRef.current)
      pollTimerRef.current = null
    }
  }, [])

  const releaseTicket = useCallback((): void => {
    const ticket = ticketRef.current
    if (ticket === null) return
    ticketRef.current = null
    void apiFetch(
      `/api/matchmaking/${encodeURIComponent(ticket)}`,
      'DELETE',
    ).catch(() => undefined)
  }, [])

  const pollStatus = useCallback(async (): Promise<void> => {
    const ticket = ticketRef.current
    if (ticket === null) return
    try {
      const status = await apiFetch<StatusResponse>(
        `/api/matchmaking/status?ticket=${encodeURIComponent(ticket)}`,
        'GET',
      )
      if (status.state === 'matched' && status.matchId !== undefined) {
        clearPollTimer()
        if (mountedRef.current) {
          ticketRef.current = null
          setState('matched')
          onClose()
          router.navigate(`/match/${status.matchId}`)
        }
        return
      }
      if (status.state === 'expired') {
        clearPollTimer()
        if (mountedRef.current) setState('bot_offer')
        return
      }
    } catch {
      clearPollTimer()
      if (mountedRef.current) setState('error')
    }
  }, [clearPollTimer, onClose, router])

  const handleEnqueue = useCallback(async (): Promise<void> => {
    setState('enqueuing')
    try {
      const result = await apiFetch<EnqueueResponse>(
        '/api/matchmaking/enqueue',
        'POST',
        {
          body: { mode: 'ranked', language: locale, ruleset: ladder },
          idempotencyKey: createIdempotencyKey(),
        },
      )
      ticketRef.current = result.ticket
      if (!mountedRef.current) {
        releaseTicket()
        return
      }
      setState('waiting')
      pollTimerRef.current = setInterval(() => {
        void pollStatus()
      }, POLL_INTERVAL_MS)
      void pollStatus()
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        window.dispatchEvent(
          new CustomEvent('runegambit:toast', {
            detail: { id: 'social.error.cap_reached' },
          }),
        )
        onClose()
        return
      }
      if (mountedRef.current) setState('error')
    }
  }, [ladder, pollStatus, releaseTicket, onClose, locale])

  useEffect(() => {
    if (!open) return
    mountedRef.current = true
    setState('idle')
    // gap-MUI-03: reset the in-memory loadout when the modal opens; the
    // persisted preset is still available via getLoadoutForMode('ranked')
    // when the picker mounts — local state is just "did the user confirm
    // a pick in THIS session?".
    setLoadout(null)
    setLoadoutPickerOpen(false)
    return () => {
      mountedRef.current = false
      clearPollTimer()
      releaseTicket()
    }
  }, [open, clearPollTimer, releaseTicket])

  const handleBotOfferConfirm = useCallback(async (): Promise<void> => {
    try {
      // gap-MUI-03: forward the user's picked loadout to the bot-fallback
      // create-match request. MatchCreateRequest accepts an optional
      // powerupLoadout (4-of-8); omit the field when no loadout was
      // picked (server falls back to the DEC-024 casual default per
      // openapi.yaml MatchCreateRequest.powerupLoadout description).
      const body: {
        mode: 'bot'
        language: string
        powerupLoadout?: readonly PowerUpName[]
      } =
        loadout !== null
          ? { mode: 'bot', language: locale, powerupLoadout: loadout }
          : { mode: 'bot', language: locale }
      const result = await apiFetch<MatchCreateResponse>(
        '/api/match/create',
        'POST',
        {
          body,
          idempotencyKey: createIdempotencyKey(),
        },
      )
      onClose()
      router.navigate(`/match/${result.matchId}`)
    } catch {
      setState('error')
    }
  }, [onClose, router, locale, loadout])

  if (!open) return null

  return (
    <>
      {/* Bottom-sheet flavor (UI-SPEC §5): scrim + grab handle + focus trap +
          scrim-tap/Escape dismissal all live in <Sheet>/ModalScrim. */}
      <Sheet
        onClose={onClose}
        testId="ranked-enqueue-modal"
        labelledBy="ranked-enqueue-modal-title"
      >
        <div
          style={{
            height: '48px',
            backgroundColor: 'var(--color-surface-deep)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            borderBottom: '3px solid var(--color-surface-stroke)',
          }}
        >
          <h2
            id="ranked-enqueue-modal-title"
            style={{
              margin: 0,
              fontFamily: 'Fredoka, sans-serif',
              fontWeight: 700,
              fontSize: 'var(--font-size-18)',
              color: 'var(--color-text-primary)',
            }}
          >
            <FormattedMessage
              id="social.lobby.ranked_modal.title"
              defaultMessage="Ranked match"
            />
          </h2>
        </div>
        <div
          style={{
            padding: '20px',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-4)',
            color: 'var(--color-text-primary)',
            fontFamily: 'Manrope, system-ui, sans-serif',
          }}
        >
          <Seg<Ladder>
            options={LADDER_OPTIONS}
            value={ladder}
            onChange={setLadder}
            ariaLabel={intl.formatMessage({
              id: 'social.lobby.ranked_modal.ladder_aria',
              defaultMessage: 'Ranked ladder',
            })}
            testId="ranked-ladder-pills"
          />
          {state === 'idle' && loadout === null && (
            <Btn
              kind="primary"
              size="lg"
              block
              testId="ranked-choose-loadout-cta"
              onClick={() => setLoadoutPickerOpen(true)}
            >
              <FormattedMessage
                id="social.lobby.ranked_modal.choose_loadout_cta"
                defaultMessage="Choose loadout"
              />
            </Btn>
          )}
          {state === 'idle' && loadout !== null && (
            <>
              <p
                data-testid="ranked-loadout-summary"
                style={{
                  margin: 0,
                  fontFamily: 'Manrope, system-ui, sans-serif',
                  fontWeight: 500,
                  fontSize: 'var(--font-size-13)',
                  color: 'var(--color-text-primary)',
                  opacity: 0.85,
                }}
              >
                {loadout
                  .map((p) =>
                    intl.formatMessage({ id: `match.powerup.name.${p}` }),
                  )
                  .join(', ')}
              </p>
              <button
                type="button"
                data-testid="ranked-change-loadout-cta"
                onClick={() => setLoadoutPickerOpen(true)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  margin: 0,
                  alignSelf: 'flex-start',
                  color: 'var(--color-text-primary)',
                  fontFamily: 'Manrope, system-ui, sans-serif',
                  fontWeight: 700,
                  fontSize: 'var(--font-size-13)',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                }}
              >
                <FormattedMessage
                  id="social.lobby.ranked_modal.change_loadout_cta"
                  defaultMessage="Change loadout"
                />
              </button>
              <Btn
                kind="primary"
                size="lg"
                block
                testId="ranked-enqueue-cta"
                onClick={() => {
                  void handleEnqueue()
                }}
              >
                <FormattedMessage
                  id="social.lobby.ranked_modal.find_cta"
                  defaultMessage="Find a match"
                />
              </Btn>
            </>
          )}
          {state === 'enqueuing' && (
            <p
              data-testid="ranked-state-enqueuing"
              aria-live="polite"
              style={{ margin: 0 }}
            >
              <FormattedMessage
                id="social.lobby.casual_modal.enqueuing"
                defaultMessage="Joining the queue…"
              />
            </p>
          )}
          {state === 'waiting' && (
            <p
              data-testid="ranked-state-waiting"
              aria-live="polite"
              style={{ margin: 0 }}
            >
              <FormattedMessage
                id="social.lobby.casual_modal.waiting"
                defaultMessage="Finding opponent…"
              />
            </p>
          )}
          {state === 'matched' && (
            <p
              data-testid="ranked-state-matched"
              aria-live="polite"
              style={{ margin: 0 }}
            >
              <FormattedMessage
                id="social.lobby.casual_modal.matched"
                defaultMessage="Match found!"
              />
            </p>
          )}
          {state === 'bot_offer' && (
            <>
              <p
                data-testid="ranked-state-bot-offer"
                aria-live="polite"
                style={{ margin: 0 }}
              >
                <FormattedMessage
                  id="social.lobby.casual_modal.bot_offer"
                  defaultMessage="Nobody's around. Play vs a bot now?"
                />
              </p>
              <Btn
                kind="primary"
                size="lg"
                block
                testId="ranked-bot-offer-confirm"
                onClick={() => {
                  void handleBotOfferConfirm()
                }}
              >
                <FormattedMessage
                  id="social.lobby.casual_modal.bot_offer_cta"
                  defaultMessage="Play vs a bot"
                />
              </Btn>
            </>
          )}
          {state === 'error' && (
            <p
              role="alert"
              data-testid="ranked-state-error"
              style={{ margin: 0, color: 'var(--color-cta-destructive)' }}
            >
              <FormattedMessage
                id="social.error.matchmaking_failed"
                defaultMessage="Matchmaking hiccupped. Try again."
              />
            </p>
          )}
        </div>
      </Sheet>
      {/* gap-MUI-03: pre-submit loadout picker. LoadoutPickerModal
          persists the picked 4-tuple via setLoadoutForMode('ranked', ...)
          internally before firing onLockIn (DEC-024 per-mode preset).
          The Ranked enqueue body does NOT carry the loadout (the
          OpenAPI schema is additionalProperties:false); the persisted
          preset is what the server reads at match-create time after
          pairing. The bot-offer fallback /api/match/create DOES carry
          powerupLoadout — see handleBotOfferConfirm. */}
      <LoadoutPickerModal
        open={loadoutPickerOpen}
        mode="ranked"
        availablePowerups={ALL_POWERUPS}
        onLockIn={(picked) => {
          setLoadout(picked)
          setLoadoutPickerOpen(false)
        }}
        onCancel={() => setLoadoutPickerOpen(false)}
      />
    </>
  )
}
